import React, { useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import eventEmitter from '../../utils/events';
import checkoutService from '../../api/checkoutService';
import { PaymentFields } from '../payment/PaymentFields';
import {
  setIsDeclinePopup,
  setDeclineReasonCode,
  setPurchaseToken,
  setCheckoutOrderID,
} from '../../redux/slices/checkoutSlice';
import {
  setCheckoutErrorMessage,
  setPurchaseCompleted,
  setSubmitSuccess,
} from '../../redux/slices/commonSlice';
import { FormData } from '../../../page-builder/interface/checkoutInterface';
import { IErrorResponse } from '../../../page-builder/interface/apiInterface';
import { resetState } from '../../../page-builder/redux/slices/postUpsellStateSlice';
import { PaymentProps } from './Payment';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import { EventPublisher } from '../../utils/publishEvent';
import { v4 as uuidv4 } from 'uuid';
import { setCartData } from '../../redux/slices/cartSlice';
import { handleEventLogger } from '../../utils/conversionAPI';
import { getIsViewIsEdit, PageType } from '../../utils/constants';
import { storePhoneCode } from '../../utils/helper';
import { useNavigate } from 'react-router-dom';
import { postUpsellPage } from '../../utils/postUpsell';

interface PaymentControllerProps {
  fields: PaymentProps;
  fromRadio?: boolean;
}

export const PaymentController: React.FC<
  PaymentControllerProps &
    ReduxSelectorProps &
    ReduxDispatchProps & {
      editMode: boolean | undefined;
    }
> = ({
  fields,
  fromRadio = false,
  useAppSelector,
  useAppDispatch,
  editMode,
}) => {
  const cartData = useAppSelector(state => state.cart?.cartData);
  const storeData = useAppSelector(state => state.store.storeData);
  const { currentPageFunnelId, pageId } = useAppSelector(
    state => state.checkout,
  );
  const postUpSellProducts = useAppSelector(
    state => state.postUpSell.postUpsellData,
  );
  const upsellData = useAppSelector(state => state.postUpSell.upsellData);

  const NextPageConfig = useAppSelector(state => state.checkout.nextpageConfig);
  const { isTermsAccepted, IsSMSConsent, IsEmailConsent } = useAppSelector(
    state => state.common,
  );
  const dispatch = useAppDispatch();
  const publish = EventPublisher.getInstance().publish;
  const [searchParams] = useSearchParams();
  const eventToken = searchParams.get('event-token');
  // const newShopUrl = storeData?.ShopURL?.replace('https://', '');
  const storeID = searchParams.get('store') || storeData?.StoreID || '';
  const igParams = searchParams.get('igParams');

  const navigate = useNavigate();
  const newSearchParams = new URLSearchParams(searchParams.toString());
  const postUpsellRedirectUrl = `/post-upsell/?${newSearchParams.toString()}`;
  const handleReturnToPayment = () => {
    dispatch(setIsDeclinePopup(false));
  };
  const smartFunnelId = useAppSelector(
    state => state.postUpSell.smartUpsellFunnelID,
  );

  const navState = useMemo(() => {
    if (!getIsViewIsEdit() && NextPageConfig?.id) {
      return {
        state: {
          id: NextPageConfig.id,
          pageVisiblity: NextPageConfig.pageVisiblity,
          pageType: NextPageConfig.pageType,
          smartFunnelId:
            NextPageConfig.pageType === PageType?.UPSELL ? smartFunnelId : '',
        },
        replace: true,
      };
    }
    return {};
  }, [
    NextPageConfig?.id,
    NextPageConfig?.pageVisiblity,
    NextPageConfig?.pageType,
    getIsViewIsEdit,
  ]);

  const handleNavigate = () => {
    navigate('/' + '?' + newSearchParams.toString(), navState);
  };
  const postCheckoutData = useCallback(
    async (input: FormData) => {
      dispatch(setCheckoutErrorMessage(null));
      dispatch(setIsDeclinePopup(false));
      if (!cartData?.ID || !storeData?.StoreID || !storeData?.ShopID) return;
      if (!getIsViewIsEdit()) {
        publish('PurchaseAttempt', {
          eventId: uuidv4(),
          eventName: 'PurchaseAttempt',
          shopId: storeData?.ShopID,
          cart: cartData,
        });
        publish('AddPaymentInfo', {
          eventId: uuidv4(),
          eventName: 'AddPaymentInfo',
          shopId: storeData?.ShopID,
          cart: cartData,
        });
      }
      const payload = {
        BillingAddress: input.BillingAddress,
        CardInfo: input.CardInfo || {
          Number: '',
          Exp: '',
          CVV: '',
        },
        ID: cartData.ID,
        IsTermsChecked: isTermsAccepted,
        IsSMSConsent,
        IsEmailConsent,
        IdempotencyKey: input.ID,
        ShopID: storeData.ShopID,
        StoreID: Number(storeData.StoreID),
        CurrentPageFunnelId: currentPageFunnelId || '',
        PageId: pageId || null,
        igParams: igParams ? JSON.parse(atob(igParams || '{}')) : undefined,
        completePerformancePayload: input?.completePerformancePayload
          ? input?.completePerformancePayload
          : {
              cart_id: cartData.ID,
              email: input?.CustomerData?.Email || '',
              complete_api_call_time: new Date().toISOString(),
              complete_click_time: new Date().toISOString(),
            },
      };
      dispatch(setSubmitSuccess(true));
      try {
        const uniqueEventId = uuidv4();
        const response = await checkoutService.checkoutComplete(
          payload,
          uniqueEventId,
        );
        if (!getIsViewIsEdit()) {
          publish('Purchase', {
            eventId: uniqueEventId,
            eventName: 'Purchase',
            shopId: storeData?.ShopID,
            cart: cartData,
            orderId: response?.data?.PurchaseToken,
            status: response?.status,
          });
          // this event is moved to backend so commenting it out for now once every this is working fine we can remove this
          // if (CallMetaApi) {
          //   handleCapi({
          //     eventName: 'Purchase',
          //     ShopID: storeData.ShopID,
          //     Cart: cartData,
          //     orderId: response?.data?.PurchaseToken,
          //   });
          // }

          handleEventLogger({
            eventType: 'payment',
            shopifyDetails: {
              shopId: storeData?.ShopID,
            },
            apiDetails: {
              payload: input,
              response: response.data,
              message: 'Payment Successfull',
            },
            eventToken: eventToken || null,
            cartDetails: cartData,
          });
        }

        dispatch(
          setCartData({
            ...cartData,
            ...input.CustomerData,
            ShippingAddress: input.Shipping,
            BillingAddress: input.BillingAddress,
          }),
        );
        dispatch(setPurchaseToken(response.data?.PurchaseToken));
        dispatch(setCheckoutOrderID(response.data?.OrderID));
        let upselldataParsed = '';
        try {
          upselldataParsed = JSON.stringify(upsellData || '{}');
        } catch (error) {
          console.log('Error parsing upsellData:', error);
        }

        if (
          postUpsellPage?.includes(storeID) &&
          postUpSellProducts &&
          postUpSellProducts.length > 0
        ) {
          navigate(postUpsellRedirectUrl);
        } else if (upsellData && upselldataParsed !== '{}') {
          handleNavigate();
        } else {
          dispatch(resetState());
          dispatch(setPurchaseCompleted(true));
          storePhoneCode('+1');
        }
      } catch (e) {
        const error = e as IErrorResponse;
        handleEventLogger({
          eventType: 'payment',
          shopifyDetails: {
            shopId: storeData?.ShopID,
          },
          apiDetails: {
            payload: input,
            response: error,
            message: 'Payment Failed',
          },
          cartDetails: cartData,
          eventToken: eventToken || null,
        });
        const errorMessage = error?.error?.response?.data?.ProcessMessage
          ? error?.error?.response?.data?.Msg
          : null;
        if (errorMessage) {
          dispatch(setCheckoutErrorMessage(errorMessage));
        } else if (error?.status === 402) {
          dispatch(setIsDeclinePopup(true));
          dispatch(
            setDeclineReasonCode(error?.message || 'Invalid card information'),
          );

          dispatch(setSubmitSuccess(false));
        } else if (
          error?.status === 403 &&
          error?.message?.toLowerCase().includes('fraud')
        ) {
          dispatch(setCheckoutErrorMessage(`Order can not be completed`));
        } else {
          dispatch(
            setCheckoutErrorMessage(
              error?.message ||
                `We encountered an error. Please try again. ${error?.status}`,
            ),
          );
        }
        if (error.status) {
          dispatch(setSubmitSuccess(false));
        }
      } finally {
        dispatch(setSubmitSuccess(false));
      }
    },
    [
      dispatch,
      cartData,
      storeData?.StoreID,
      storeData?.ShopID,
      postUpSellProducts,
      NextPageConfig,
      smartFunnelId,
      upsellData,
      IsSMSConsent,
      IsEmailConsent,
      isTermsAccepted,
    ],
  );

  useEffect(() => {
    eventEmitter.addListener('completePurchase', postCheckoutData);
    return () => {
      eventEmitter.removeListener('completePurchase', postCheckoutData);
    };
  }, [postCheckoutData]);
  return (
    <>
      <PaymentFields
        handleReturnToPayment={handleReturnToPayment}
        fields={fields}
        editMode={editMode}
        fromRadio={fromRadio}
        useAppSelector={useAppSelector}
        useAppDispatch={useAppDispatch}
        storeData={storeData}
        cartData={cartData}
      />
    </>
  );
};
